import {CoreDI} from '@fantasmagames/core/src/di/CoreDI';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {System} from '@fantasmagames/core/src/System';
import {QuerystringHandler} from '@fantasmagames/core/src/utils/QuerystringHandler';
import DI from '@fantasmagames/slot/src/common/inversify/constants/DI';
import {ISettings} from '@fantasmagames/slot/src/common/settings/_interfaces/ISettings';
import {View, ViewTypes} from '@fantasmagames/slot/src/common/view/View';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {IViewsManager, Layers, ViewsMap, ViewsZOrderMap} from '@fantasmagames/slot/src/common/view/_interfaces/IViewsManager';
import {Layout} from '@fantasmagames/slot/src/layout/Layout';
import {BackgroundView} from '@fantasmagames/slot/src/modules/background/BackgroundView';
import {BigWinView} from '@fantasmagames/slot/src/modules/bigwin/BigWinView';
import {CheatView} from '@fantasmagames/slot/src/modules/cheat/CheatView';
import {InfoView} from '@fantasmagames/slot/src/modules/hud/info/InfoView';
import {AutoPlayButtonView} from '@fantasmagames/slot/src/modules/keypad/autoplay/AutoPlayButtonView';
import {BetButtonView} from '@fantasmagames/slot/src/modules/keypad/bet/BetButtonView';
import {MenuButtonView} from '@fantasmagames/slot/src/modules/keypad/menuButton/MenuButtonView';
import {SpinButtonView} from '@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonView';
import {StopAutoplayButtonView} from '@fantasmagames/slot/src/modules/keypad/stopautoplaybutton/StopAutoplayButtonView';
import {LogotypeView} from '@fantasmagames/slot/src/modules/logotype/LogotypeView';
import {FreeRoundsScreenView} from '@fantasmagames/slot/src/modules/microgaming/freerounds/FreeRoundsScreenView';
import {InFreeRoundsView} from '@fantasmagames/slot/src/modules/microgaming/inFreeRounds/InFreeRoundsView';
import {OrientationView} from '@fantasmagames/slot/src/modules/orientation/OrientationView';
import {ParticleView} from '@fantasmagames/slot/src/modules/particles/ParticleView';
import {AutoplayMenuView} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuView';
import {BetlevelMenuView} from '@fantasmagames/slot/src/modules/popupmenus/betmenu/BetlevelMenuView';
import {InfoPopupView} from '@fantasmagames/slot/src/modules/popupmenus/info/InfoPopupView';
import {ReelConstants} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {ReelsView} from '@fantasmagames/slot/src/modules/reels/spin/ReelsView';
import {SwipeInteractionView} from '@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionView';
import {WinPresentationView} from '@fantasmagames/slot/src/modules/winpresentation/WinPresentationView';
import {Container, inject, injectable} from 'inversify';
import {RelaxInFreeRoundsView} from '@fantasmagames/slot/src/modules/relax/freeroundsingame/RelaxInFreeRoundsView';
import {RelaxFreeRoundsView} from '@fantasmagames/slot/src/modules/relax/freerounds/RelaxFreeRoundsView';

/**
 * stores and gives access to all the game views
 */
@injectable()
export class ViewManager implements IViewsManager {
	protected _views: ViewsMap = {};

	protected _zOrder: ViewsZOrderMap = {};

	@inject(CoreDI.System)
	protected _system: System;

	@inject(DI.ISettings)
	protected _settings: ISettings;

	@inject(CoreDI.ResourceLoader)
	protected _resourcesLoader: ResourcesLoader;

	@inject(DI.Layout)
	protected _layout: Layout;

	protected _container: Container;

	protected _hasFewMobileViews: boolean = false;

	public init(): void {
		this._container = this._system.container;

		// this.setupReelsViewConstants(reelsConfigSprite);

		this.createBackgroundView();
		this.createOrientationNotificationView();
		this.createSpinButtonView();
		this.createAutoPlayButtonView();
		this.createBetButtonView();
		this.createMenuButtonView();
		this.createReelsView();
		this.createWinPresentationView();
		this.createBigWinView();
		this.createParticleView();
		this.createBetlevelMenuView();
		this.createAutoplayMenuView();
		this.createStopAutoplayButtonView();
		this.creareInfoPopupView();
		this.createSwipeInteractionView();
		this.createGameInfoView();
		this.createLogotypeView();
		this.createGameSpecificViews();
		this.createPlatformSpecificViews();

		if (!__PRODUCTION__) {
			this.createCheatsPageView();
			this.createStatisticsWindowView();
		}

		for (let layer: number = 0; layer < Layers.TOTAL_LAYERS; layer++) {
			for (const key in this._views) {
				if (this._views.hasOwnProperty(key) && this._zOrder[key] === layer) {
					this._views[key].init();
				}
			}
		}

		this.postInitialize();
	}

	public getView(name: string): View {
		return this._views[name];
	}

	protected createPlatformSpecificViews(): void {
		const query: QuerystringHandler = new QuerystringHandler();
		const platform: string = query.getValue('platform');
	}

	protected createRelaxFreeRoundsView(): void {
		const viewName: string = Views.RELAX_FREEROUNDS_VIEW;

		const view: RelaxFreeRoundsView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.SYSTEM_POPUPS_LAYER;
	}

	protected createRelaxInFreeRoundsView(): void {
		const viewName: string = Views.RELAX_FREEROUNDS_INGAME_VIEW;

		const view: RelaxInFreeRoundsView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.KEYPAD_LAYER;
	}

	protected createLogotypeView(): void {
		const logoViewName: string = this._system.shouldUseMultipleViews() ? Views.LOGOTYPE_VIEW_MOBILE : Views.LOGOTYPE_VIEW;
		const view: LogotypeView = this._container.get(logoViewName);
		this._views[logoViewName] = view;
		this._zOrder[logoViewName] = Layers.LOGOTYPE_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.LOGOTYPE_VIEW);
		}
	}

	protected createGameInfoView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.INFO_VIEW_MOBILE : Views.INFO_VIEW;
		const view: InfoView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.GUI_INFO_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.INFO_VIEW);
		}
	}

	protected createStatisticsWindowView(): void {
		const viewName: string =
			/* this._system.shouldUseMultipleViews() ? Views.STATISTICS_WINDOW_VIEW_MOBILE :*/ Views.STATISTICS_WINDOW_VIEW;

		const view: SwipeInteractionView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.SYSTEM_POPUPS_LAYER;

		/*if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.STATISTICS_WINDOW_VIEW);
		}*/
	}

	protected createSwipeInteractionView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.SWIPE_INTERACTION_VIEW_MOBILE : Views.SWIPE_INTERACTION_VIEW;

		const view: SwipeInteractionView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.REELS_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.SWIPE_INTERACTION_VIEW);
		}
	}

	protected creareInfoPopupView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.INFO_POPUP_VIEW_MOBILE : Views.INFO_POPUP_VIEW;

		const view: InfoPopupView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.INFORMATION_POPUPS_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.INFO_POPUP_VIEW);
		}
	}

	protected createStopAutoplayButtonView(): void {
		const viewName: string = this._system.shouldUseMultipleViews()
			? Views.STOP_AUTOPLAY_BUTTON_VIEW_MOBILE
			: Views.STOP_AUTOPLAY_BUTTON_VIEW;

		const view: StopAutoplayButtonView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.KEYPAD_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.STOP_AUTOPLAY_BUTTON_VIEW);
		}
	}

	protected createCheatsPageView(): void {
		const viewName: string = /* this._system.shouldUseMultipleViews() ? Views.CHEATS_VIEW_MOBILE :*/ Views.CHEATS_VIEW;

		const view: CheatView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.CHEATS_LAYER;

		/*if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.CHEATS_VIEW);
		}*/
	}

	protected createBigWinView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.BIG_WIN_VIEW_MOBILE : Views.BIG_WIN_VIEW;

		const view: BigWinView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.POPUPS_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.BIG_WIN_VIEW);
		}
	}

	protected createBackgroundView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.BACKGROUND_VIEW_MOBILE : Views.BACKGROUND_VIEW;

		const view: BackgroundView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.BACKGROUND_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.BACKGROUND_VIEW);
		}
	}

	protected createSpinButtonView(): void {
		const keypadSpinButtonViewName: string = this._system.shouldUseMultipleViews()
			? Views.SPIN_BUTTON_VIEW_MOBILE
			: Views.SPIN_BUTTON_VIEW;
		const keypadSpinButtonView: SpinButtonView = this._container.get(keypadSpinButtonViewName);
		this._views[keypadSpinButtonViewName] = keypadSpinButtonView;
		this._zOrder[keypadSpinButtonViewName] = Layers.KEYPAD_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			keypadSpinButtonView.addStandardMobileViews(Views.SPIN_BUTTON_VIEW);
		}
	}

	protected createAutoPlayButtonView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.AUTOPLAY_VIEW_MOBILE : Views.AUTOPLAY_VIEW;

		const view: AutoPlayButtonView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.KEYPAD_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.AUTOPLAY_VIEW);
		}
	}

	protected createMenuButtonView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.MENUBUTTON_VIEW_MOBILE : Views.MENUBUTTON_VIEW;

		const view: MenuButtonView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.OVER_KEYPAD_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.MENUBUTTON_VIEW);
		}
	}

	protected createBetButtonView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.BET_VIEW_MOBILE : Views.BET_VIEW;

		const view: BetButtonView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.KEYPAD_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.BET_VIEW);
		}
	}

	protected createBetlevelMenuView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.BET_LEVEL_MENU_VIEW_MOBILE : Views.BET_LEVEL_MENU_VIEW;

		const view: BetlevelMenuView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.MENU_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.BET_LEVEL_MENU_VIEW);
		}
	}

	protected createAutoplayMenuView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.AUTOPLAY_MENU_VIEW_MOBILE : Views.AUTOPLAY_MENU_VIEW;

		const view: AutoplayMenuView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.MENU_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.AUTOPLAY_MENU_VIEW);
		}
	}

	protected createReelsView(): void {
		this.setupLayout();
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.REELS_VIEW_MOBILE : Views.REELS_VIEW;

		const view: ReelsView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.REELS_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.REELS_VIEW);
		}
	}

	protected setupLayout(): void {
		if (this.canInitializeFromLayout()) {
			ReelConstants.initializeFromLayout(this._layout);
		} else {
			throw new Error('ViewManager::createReelsView can not initialize from layout');
		}
	}

	protected canInitializeFromLayout(viewType: ViewTypes = ViewTypes.DESKTOP): boolean {
		return (
			this._layout.isLoaded() &&
			this._layout.getPosition('REEL', viewType) !== null &&
			this._layout.getSize('SYMBOL_1', viewType) !== null
		);
	}

	protected createWinPresentationView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.WIN_PRESENTATION_VIEW_MOBILE : Views.WIN_PRESENTATION_VIEW;

		const view: WinPresentationView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.WIN_TEXT_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.WIN_PRESENTATION_VIEW);
		}
	}

	protected createParticleView(): void {
		const viewName: string = this._system.shouldUseMultipleViews() ? Views.PARTICLE_VIEW_MOBILE : Views.PARTICLE_VIEW;

		const view: ParticleView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.GUI_INFO_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.PARTICLE_VIEW);
		}
	}

	protected createOrientationNotificationView(): void {
		const view: OrientationView = this._container.get(Views.ORIENTATION_NOTIFICATION_VIEW);
		this._views[Views.ORIENTATION_NOTIFICATION_VIEW] = view;
		this._zOrder[Views.ORIENTATION_NOTIFICATION_VIEW] = Layers.POPUPS_LAYER;
	}

	protected createMicrogamingFreeRoundsView(): void {
		const viewName: string = this._system.shouldUseMultipleViews()
			? Views.MICROGAMING_FREEROUNDS_VIEW_MOBILE
			: Views.MICROGAMING_FREEROUNDS_VIEW;

		const view: FreeRoundsScreenView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.SYSTEM_POPUPS_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.MICROGAMING_FREEROUNDS_VIEW);
		}
	}

	protected createMicrogamingFreeRoundsInGameView(): void {
		const viewName: string = this._system.shouldUseMultipleViews()
			? Views.MICROGAMING_INFREEROUNDS_VIEW_MOBILE
			: Views.MICROGAMING_INFREEROUNDS_VIEW;

		const view: InFreeRoundsView = this._container.get(viewName);
		this._views[viewName] = view;
		this._zOrder[viewName] = Layers.KEYPAD_LAYER;

		if (this._system.shouldUseMultipleViews()) {
			view.addStandardMobileViews(Views.MICROGAMING_INFREEROUNDS_VIEW);
		}
	}

	protected createGameSpecificViews(): void {}

	protected setupReelsViewConstants(reelsConfigSprite: PIXI.Sprite, viewType: ViewTypes): void {
		let reelIndex = 0;
		let currentReel;
		do {
			currentReel = reelsConfigSprite.getChildByName('reel' + reelIndex);
			if (currentReel) {
				ReelConstants.addReelsConfig(
					{
						x: currentReel.x,
						y: currentReel.y,
						symsPositions: [],
					},
					viewType,
				);

				let currentSym;
				let symIndex = 0;
				do {
					currentSym = currentReel.getChildByName('sym' + symIndex);
					if (currentSym) {
						ReelConstants.getReels(viewType)[reelIndex].symsPositions.push({
							x: currentReel.x + currentSym.x,
							y: currentReel.y + currentSym.y,
						});
						symIndex++;
					}
				} while (currentSym);

				reelIndex++;
			}
		} while (currentReel);

		ReelConstants.REELS_AMOUNT = ReelConstants.getReels(viewType).length;
		ReelConstants.ROW_AMOUNT = ReelConstants.getReels(viewType)[0].symsPositions.length;
	}

	protected postInitialize(): void {
		this.translate();

		if (!__PRODUCTION__) {
			(document as any).VIEWMANAGER = this;
		}
	}

	protected translate(): void {
		for (const key in this._views) {
			if (this._views.hasOwnProperty(key)) {
				this._views[key].translator.englishObject = this._resourcesLoader.getEnglishStrings();
				this._views[key].translator.localeObjects = this._resourcesLoader.getTranslation();
				this._views[key].translate();
			}
		}
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/common/view/ViewManager.ts